<template>
  <!-- no left margin: the shell's .rs-content already supplies a 16px gutter,
       matching the 16px on the right -->
  <div style="margin: 0 16px 20px 0">
    <FiPageRail title="SMS Usage Dashboard" />

    <div style="position: relative; font-size: 20px; font-weight: bold; gap: 6px;" class="align-right d-flex align-items-center justify-content-center flex-wrap">
      <a href="#" class="arrows" v-on:click.prevent="shift_month(-1)">&lt;</a>
      &nbsp;&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="start_date" :config="date_options" :placeholder="start_date"/>
      &nbsp;to&nbsp;<date-picker style="font-size: 20px; font-weight: bold; width:140px" v-model="end_date" :config="date_options" :placeholder="end_date"/>
      &nbsp;<a href="#" class="arrows" v-on:click.prevent="shift_month(1)">&gt;</a>
      &nbsp;&nbsp;|&nbsp;&nbsp;Tenant&nbsp;<b-form-select v-model="selected_tenant" :options="computed_tenant_options" style="width: 220px;" @change="load_data()"></b-form-select>
      &nbsp;&nbsp;|&nbsp;&nbsp;<b-button v-on:click.prevent="load_data()">Search</b-button>
    </div>
    <br/>

    <div v-if="loading" class="text-center" style="margin-top: 40px;">
      <b-spinner style="width: 3rem; height: 3rem;" label="Loading..."></b-spinner>
    </div>

    <!-- ===================== TOTALS ===================== -->
    <div v-if="!loading && usage_data" class="d-flex justify-content-center flex-wrap" style="gap: 12px; margin-bottom: 10px;">
      <div v-for="tile in totals_tiles" :key="tile.label" style="border: 1px solid #ccc; background-color: #fafafa; padding: 8px 18px; text-align: center; min-width: 120px;">
        <div style="font-size: 22px; font-weight: bold;">{{ tile.value }}</div>
        <div style="font-size: 13px; color: #666;">{{ tile.label }}</div>
      </div>
    </div>

    <!-- ===================== MESSAGES BY DATE ===================== -->
    <div v-if="!loading && usage_data" style="margin-top: 20px;">
      <div style="font-size: 22px; font-weight: bold; color: white; background-color: #37474f; padding: 8px 12px; margin-bottom: 0; cursor: pointer; display: flex; align-items: center; justify-content: space-between;"
           v-on:click="show_chart_section = !show_chart_section">
        <span>Messages By Date</span>
        <span style="font-size: 14px;">{{ show_chart_section ? '▲' : '▼' }}</span>
      </div>
      <div v-if="show_chart_section" style="padding-top: 15px;">
        <div class="d-flex align-items-center justify-content-center" style="margin-bottom: 10px;">
          <span style="font-size: 16px; font-weight: bold; margin-right: 10px;">Filter Chart:</span>
          <b-form-select v-model="selected_chart_tenant" :options="computed_chart_tenant_options" style="width: 300px;" @change="update_chart"></b-form-select>
        </div>
        <dashboard-chart
            v-if="messages_by_date_chart"
            :loading="loading"
            :chart_data="messages_by_date_chart"
            chart_type="LineChart"
            :graph_title="chart_title"
            date_format="MM/dd"
            graph_height="300"
        />
      </div>
    </div>

    <!-- ===================== BILLING BY TENANT ===================== -->
    <div v-if="!loading && usage_data && by_tenant_items.length > 0" style="margin-top: 30px;">
      <div style="font-size: 20px; font-weight: bold; color: white; background-color: var(--fi-rail-accent); padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px;"
           v-on:click="show_tenant_table = !show_tenant_table">
        <span>Billing By Tenant ({{ by_tenant_items.length }})</span>
        <b-button size="sm" variant="light" v-on:click.stop="downloadUsageCsv(by_tenant_items, tenant_fields, 'sms_usage_by_tenant')">Download CSV</b-button>
      </div>
      <b-table
          v-if="show_tenant_table"
          :items="_limit(by_tenant_items, 'tenant')"
          :fields="tenant_fields"
          striped hover small bordered
          sort-by="outbound"
          :sort-desc="true"
          style="margin-top: 10px;"
      >
        <template slot="outbound" slot-scope="data">{{ fmt_int(data.value) }}</template>
        <template slot="inbound" slot-scope="data">{{ fmt_int(data.value) }}</template>
        <template slot="parts" slot-scope="data">{{ fmt_int(data.value) }}</template>
        <template slot="delivered" slot-scope="data">{{ fmt_int(data.value) }}</template>
        <template slot="failed" slot-scope="data">{{ fmt_int(data.value) }}</template>
        <template slot="price_usd" slot-scope="data">{{ fmt_usd_cents(data.value) }}</template>
        <template slot="est_monthly" slot-scope="data">{{ data.value === null ? '—' : fmt_usd_cents(data.value) }}</template>
      </b-table>
      <div v-if="show_tenant_table && by_tenant_items.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
        <a href="#" v-on:click.prevent="$set(table_expanded, 'tenant', !table_expanded['tenant'])" style="color: var(--fi-rail-accent); text-decoration: none;">
          {{ table_expanded['tenant'] ? '▲ Show less' : '▼ Show ' + (by_tenant_items.length - 10) + ' more' }}
        </a>
      </div>
    </div>

    <!-- ===================== BY MESSAGE TYPE ===================== -->
    <div v-if="!loading && usage_data && by_context_items.length > 0" style="margin-top: 30px;">
      <div style="font-size: 20px; font-weight: bold; color: white; background-color: var(--fi-rail-accent); padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px;"
           v-on:click="show_context_table = !show_context_table">
        <span>Usage By Message Type ({{ by_context_items.length }})</span>
        <b-button size="sm" variant="light" v-on:click.stop="downloadUsageCsv(by_context_items, context_fields, 'sms_usage_by_type')">Download CSV</b-button>
      </div>
      <b-table
          v-if="show_context_table"
          :items="_limit(by_context_items, 'context')"
          :fields="context_fields"
          striped hover small bordered
          sort-by="messages"
          :sort-desc="true"
          style="margin-top: 10px;"
      >
        <template slot="messages" slot-scope="data">{{ fmt_int(data.value) }}</template>
        <template slot="parts" slot-scope="data">{{ fmt_int(data.value) }}</template>
        <template slot="failed" slot-scope="data">{{ fmt_int(data.value) }}</template>
        <template slot="price_usd" slot-scope="data">{{ fmt_usd_cents(data.value) }}</template>
        <template slot="est_monthly" slot-scope="data">{{ data.value === null ? '—' : fmt_usd_cents(data.value) }}</template>
      </b-table>
      <div v-if="show_context_table && by_context_items.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
        <a href="#" v-on:click.prevent="$set(table_expanded, 'context', !table_expanded['context'])" style="color: var(--fi-rail-accent); text-decoration: none;">
          {{ table_expanded['context'] ? '▲ Show less' : '▼ Show ' + (by_context_items.length - 10) + ' more' }}
        </a>
      </div>
    </div>

    <!-- ===================== BY DATE ===================== -->
    <div v-if="!loading && usage_data && by_date_items.length > 0" style="margin-top: 30px;">
      <div style="font-size: 20px; font-weight: bold; color: white; background-color: var(--fi-rail-accent); padding: 5px 10px; cursor: pointer; display: flex; align-items: center; justify-content: space-between; gap: 8px;"
           v-on:click="show_date_table = !show_date_table">
        <span>Usage By Date ({{ by_date_items.length }})</span>
        <b-button size="sm" variant="light" v-on:click.stop="downloadUsageCsv(by_date_items, date_fields, 'sms_usage_by_date')">Download CSV</b-button>
      </div>
      <b-table
          v-if="show_date_table"
          :items="_limit(by_date_items, 'date')"
          :fields="date_fields"
          striped hover small bordered
          sort-by="date"
          :sort-desc="true"
          style="margin-top: 10px;"
      >
        <template slot="outbound" slot-scope="data">{{ fmt_int(data.value) }}</template>
        <template slot="inbound" slot-scope="data">{{ fmt_int(data.value) }}</template>
        <template slot="parts" slot-scope="data">{{ fmt_int(data.value) }}</template>
        <template slot="price_usd" slot-scope="data">{{ fmt_usd_cents(data.value) }}</template>
      </b-table>
      <div v-if="show_date_table && by_date_items.length > 10" class="text-center" style="padding: 4px 0 6px; font-size: 13px;">
        <a href="#" v-on:click.prevent="$set(table_expanded, 'date', !table_expanded['date'])" style="color: var(--fi-rail-accent); text-decoration: none;">
          {{ table_expanded['date'] ? '▲ Show less' : '▼ Show ' + (by_date_items.length - 10) + ' more' }}
        </a>
      </div>
    </div>

    <!-- ===================== MESSAGE DETAIL ===================== -->
    <div v-if="!loading && has_loaded" style="margin-top: 30px;">
      <div style="font-size: 22px; font-weight: bold; color: white; background-color: #2c3e50; padding: 8px 12px; margin-bottom: 0; cursor: pointer; display: flex; align-items: center; justify-content: space-between;"
           v-on:click="show_messages_section = !show_messages_section">
        <span>Message Detail</span>
        <span style="font-size: 14px;">{{ show_messages_section ? '▲' : '▼' }}</span>
      </div>
      <div v-if="show_messages_section" style="padding-top: 15px;">
        <div class="d-flex align-items-center justify-content-center flex-wrap" style="gap: 8px; margin-bottom: 10px;">
          <span style="font-size: 16px; font-weight: bold;">Status</span>
          <b-form-select v-model="msg_status" :options="msg_status_options" style="width: 160px;"></b-form-select>
          <span style="font-size: 16px; font-weight: bold;">Direction</span>
          <b-form-select v-model="msg_direction" :options="msg_direction_options" style="width: 160px;"></b-form-select>
          <span style="font-size: 16px; font-weight: bold;">Type</span>
          <b-form-select v-model="msg_context" :options="msg_context_options" style="width: 240px;"></b-form-select>
          <b-button size="sm" v-on:click.prevent="load_messages(true)">Load Messages</b-button>
          <b-button
              v-if="messages.length > 0"
              size="sm"
              variant="light"
              v-on:click.prevent="downloadUsageCsv(messages, message_csv_fields, 'sms_messages')"
          >Download CSV</b-button>
        </div>

        <div v-if="messages_loading" class="text-center" style="margin: 20px;">
          <b-spinner label="Loading..."></b-spinner>
        </div>

        <b-table
            v-if="messages.length > 0"
            :items="messages"
            :fields="message_fields"
            striped hover small bordered
            style="margin-top: 10px; font-size: 13px;"
        >
          <template slot="price_usd" slot-scope="data">{{ data.value ? fmt_usd_precise(data.value) : '—' }}</template>
          <template slot="provider_response" slot-scope="data">{{ data.value || '—' }}</template>
          <template slot="phone_carrier" slot-scope="data">{{ data.value || '—' }}</template>
          <template slot="context" slot-scope="data">{{ data.value || '—' }}</template>
        </b-table>

        <div v-if="!messages_loading && messages.length === 0 && messages_loaded" class="text-center" style="margin: 20px; color: #666;">
          No messages found for the selected filters.
        </div>

        <div v-if="messages_cursor && !messages_loading" class="text-center" style="margin: 10px;">
          <b-button size="sm" v-on:click.prevent="load_messages(false)">Load More</b-button>
        </div>
      </div>
    </div>

    <div v-if="!loading && !usage_data && has_loaded" class="text-center" style="margin-top: 40px;">
      <h4>No SMS usage data found for the selected date range.</h4>
    </div>
  </div>
</template>

<script>
import { get_sms_usage, get_sms_usage_messages } from '../networking/apis'
import { mapState } from 'vuex'
import { justDate } from '../helpers/dates'
import moment from 'moment'
import DashboardChart from './DashboardChart'

export default {
  name: 'sms_usage_dashboard',
  components: {
    DashboardChart,
  },
  data() {
    return {
      loading: false,
      has_loaded: false,
      usage_data: null,
      messages_by_date_chart: null,
      selected_chart_tenant: '',
      selected_tenant: '',
      start_date: justDate(moment().startOf('month')),
      end_date: justDate(moment().add(1, 'days')),
      show_chart_section: true,
      show_tenant_table: true,
      show_context_table: true,
      show_date_table: false,
      show_messages_section: false,
      table_expanded: {},
      messages: [],
      messages_cursor: null,
      messages_loading: false,
      messages_loaded: false,
      msg_status: '',
      msg_status_options: [
        { value: '', text: 'All Statuses' },
        { value: 'delivered', text: 'Delivered' },
        { value: 'failed', text: 'Failed' },
        { value: 'sent', text: 'Sent (no receipt)' },
        { value: 'received', text: 'Received' },
      ],
      msg_direction: '',
      msg_direction_options: [
        { value: '', text: 'All Directions' },
        { value: 'outbound', text: 'Outbound' },
        { value: 'inbound', text: 'Inbound' },
      ],
      msg_context: '',
      date_options: {
        format: 'MM-DD-YYYY',
        useCurrent: true,
        minDate: moment().subtract(2, 'years'),
        maxDate: moment().add(1, 'days'),
        keepOpen: false,
        showClear: true,
        showClose: true,
      },
      context_fields: [
        { key: 'context', label: 'Message Type', sortable: true },
        { key: 'messages', label: 'Messages', sortable: true },
        { key: 'parts', label: 'Parts', sortable: true },
        { key: 'failed', label: 'Failed', sortable: true },
        { key: 'price_usd', label: 'Actual Cost', sortable: true },
        { key: 'est_monthly', label: 'Est. Monthly Cost', sortable: true },
      ],
      date_fields: [
        { key: 'date', label: 'Date', sortable: true },
        { key: 'outbound', label: 'Outbound', sortable: true },
        { key: 'inbound', label: 'Inbound', sortable: true },
        { key: 'parts', label: 'Parts', sortable: true },
        { key: 'price_usd', label: 'Actual Cost', sortable: true },
      ],
      message_fields: [
        { key: 'created_at', label: 'Sent/Received', sortable: true, formatter: v => v ? moment(String(v)).format('MM/DD HH:mm') : '—' },
        { key: 'tenant_id', label: 'Tenant', sortable: true },
        { key: 'direction', label: 'Direction', sortable: true },
        { key: 'status', label: 'Status', sortable: true },
        { key: 'context', label: 'Type', sortable: true },
        { key: 'destination_number', label: 'Destination', sortable: true },
        { key: 'parts', label: 'Parts', sortable: true },
        { key: 'price_usd', label: 'Cost', sortable: true },
        { key: 'phone_carrier', label: 'Carrier', sortable: true },
        { key: 'provider_response', label: 'Carrier Response', sortable: false },
      ],
      message_csv_fields: [
        { key: 'created_at', label: 'Created At' },
        { key: 'message_id', label: 'Message ID' },
        { key: 'tenant_id', label: 'Tenant' },
        { key: 'direction', label: 'Direction' },
        { key: 'status', label: 'Status' },
        { key: 'context', label: 'Type' },
        { key: 'destination_number', label: 'Destination' },
        { key: 'origination_number', label: 'Origination' },
        { key: 'parts', label: 'Parts' },
        { key: 'price_usd', label: 'Cost USD' },
        { key: 'phone_carrier', label: 'Carrier' },
        { key: 'provider_response', label: 'Carrier Response' },
        { key: 'source', label: 'Source' },
      ],
    }
  },
  computed: {
    ...mapState({
      user: state => state.profile.user
    }),
    computed_tenant_options() {
      const options = [{ value: '', text: 'All Tenants' }]
      if (this.user && this.user.tenant_options && this.user.tenant_options.length > 0) {
        this.user.tenant_options.forEach(opt => {
          options.push({ value: opt.value, text: opt.text })
        })
      }
      return options
    },
    tenant_id_to_name() {
      const map = {}
      if (this.user && this.user.tenant_options) {
        this.user.tenant_options.forEach(opt => {
          if (opt && opt.value) map[opt.value] = opt.text || opt.value
        })
      }
      return map
    },
    est_monthly_cost() {
      return this._est_monthly_from_series((this.usage_data && this.usage_data.by_date) || [])
    },
    totals_tiles() {
      const t = (this.usage_data && this.usage_data.totals) || {}
      return [
        { label: 'Outbound', value: this.fmt_int(t.outbound) },
        { label: 'Inbound', value: this.fmt_int(t.inbound) },
        { label: 'Parts', value: this.fmt_int(t.parts) },
        { label: 'Delivered', value: this.fmt_int(t.delivered) },
        { label: 'Failed', value: this.fmt_int(t.failed) },
        { label: 'Pending Receipt', value: this.fmt_int(t.pending) },
        { label: 'Actual Cost', value: this.fmt_usd(t.price_usd) },
        { label: 'Est. Monthly Cost', value: this.est_monthly_cost === null ? '—' : this.fmt_usd(this.est_monthly_cost) },
      ]
    },
    by_tenant_items() {
      if (!this.usage_data || !this.usage_data.by_tenant) return []
      const by_tenant_date = this.usage_data.by_tenant_date || {}
      return this.usage_data.by_tenant.map(r => ({
        ...r,
        name: this.tenant_id_to_name[r.tenant_id] || r.tenant_id,
        est_monthly: this._est_monthly_from_series(by_tenant_date[r.tenant_id]),
      }))
    },
    tenant_fields() {
      return [
        { key: 'name', label: 'Tenant', sortable: true },
        { key: 'outbound', label: 'Outbound', sortable: true },
        { key: 'inbound', label: 'Inbound', sortable: true },
        { key: 'parts', label: 'Parts', sortable: true },
        { key: 'delivered', label: 'Delivered', sortable: true },
        { key: 'failed', label: 'Failed', sortable: true },
        { key: 'price_usd', label: 'Actual Cost', sortable: true },
        { key: 'est_monthly', label: 'Est. Monthly Cost', sortable: true },
      ]
    },
    by_context_items() {
      const rows = (this.usage_data && this.usage_data.by_context) || []
      const by_context_date = (this.usage_data && this.usage_data.by_context_date) || {}
      return rows.map(r => ({
        ...r,
        est_monthly: this._est_monthly_from_series(by_context_date[r.context]),
      }))
    },
    by_date_items() {
      return (this.usage_data && this.usage_data.by_date) || []
    },
    computed_chart_tenant_options() {
      const options = [{ value: '', text: 'All Tenants' }]
      if (this.usage_data && this.usage_data.by_tenant) {
        this.usage_data.by_tenant.forEach(r => {
          const total = Number(r.outbound || 0) + Number(r.inbound || 0)
          options.push({
            value: r.tenant_id,
            text: `${this.tenant_id_to_name[r.tenant_id] || r.tenant_id} (${total.toLocaleString()})`,
          })
        })
      }
      return options
    },
    chart_title() {
      if (this.selected_chart_tenant) {
        return 'Messages By Date — ' + (this.tenant_id_to_name[this.selected_chart_tenant] || this.selected_chart_tenant)
      }
      return 'Messages By Date'
    },
    msg_context_options() {
      // options reflect whatever context values actually exist in the range
      const options = [{ value: '', text: 'All Types' }]
      this.by_context_items.forEach(r => {
        options.push({ value: r.context, text: `${r.context} (${Number(r.messages || 0).toLocaleString()})` })
      })
      return options
    },
  },
  async created() {
    await this.load_data()
  },
  methods: {
    fmt_int(v) {
      return Number(v || 0).toLocaleString()
    },
    fmt_usd(v) {
      // totals tile shows whole dollars; the per-tenant/type/date tables use
      // fmt_usd_cents and the drill-down uses fmt_usd_precise
      return '$' + Math.round(Number(v || 0)).toLocaleString()
    },
    fmt_usd_cents(v) {
      return '$' + Number(v || 0).toFixed(2)
    },
    fmt_usd_precise(v) {
      return '$' + Number(v || 0).toFixed(4)
    },
    _limit(items, key) {
      if (!items) return []
      return this.table_expanded[key] ? items : items.slice(0, 10)
    },
    _est_monthly_from_series(series) {
      // daily average over the most recent 7 days of data, scaled to a full
      // month. the per-date series omit days with no messages, so the window
      // is counted in calendar days -- a quiet day is a real zero, not a
      // missing sample. shared by the totals tile and the table columns so
      // every estimate on the page uses one formula.
      const rows = series || []
      if (!rows.length) return null
      const start = moment(this.start_date, 'MM-DD-YYYY').startOf('day')
      const end = moment(this.end_date, 'MM-DD-YYYY').startOf('day')
      // the range can run past today (the default end date is tomorrow) and
      // future days carry no cost yet, so anchor on the last real day
      const anchor = moment.min(end, moment().startOf('day'))
      if (!start.isValid() || !anchor.isValid() || anchor.isBefore(start)) return null
      // 7-day window, shortened when the selected range is shorter than that
      const window_start = moment.max(start, anchor.clone().subtract(6, 'days'))
      const window_days = anchor.diff(window_start, 'days') + 1
      if (window_days < 1) return null
      let window_cost = 0
      rows.forEach(r => {
        const d = moment(String(r.date), 'YYYY-MM-DD').startOf('day')
        if (d.isValid() && !d.isBefore(window_start) && !d.isAfter(anchor)) {
          window_cost += Number(r.price_usd || 0)
        }
      })
      return (window_cost / window_days) * anchor.daysInMonth()
    },
    _fieldKeys(fields) {
      return fields.map(f => (typeof f === 'string' ? f : f.key))
    },
    _fieldLabels(fields) {
      return fields.map(f => (typeof f === 'string' ? f : (f.label || f.key)))
    },
    _csvEscapeField(val) {
      if (val == null || val === '') return ''
      const s = String(val)
      if (/[",\r\n]/.test(s)) return `"${s.replace(/"/g, '""')}"`
      return s
    },
    _usageCsvDateSuffix() {
      const sd = moment(this.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const ed = moment(this.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
      return `${sd}_to_${ed}`
    },
    downloadUsageCsv(items, fields, baseFilename) {
      if (!items || !items.length || !fields || !fields.length) return
      const keys = this._fieldKeys(fields)
      const headers = this._fieldLabels(fields)
      const lines = [headers.map(h => this._csvEscapeField(h)).join(',')]
      for (let i = 0; i < items.length; i++) {
        const row = items[i]
        lines.push(keys.map(k => {
          // cost columns carry the $ symbol in exports too; full precision so
          // the export remains usable for reconciliation
          if ((k === 'price_usd' || k === 'est_monthly') && row[k] != null && row[k] !== '') return this._csvEscapeField(this.fmt_usd_precise(row[k]))
          return this._csvEscapeField(row[k])
        }).join(','))
      }
      const safe = String(baseFilename || 'usage').replace(/[^\w-]+/g, '_')
      const blob = new Blob([`\uFEFF${lines.join('\r\n')}`], { type: 'text/csv;charset=utf-8;' })
      const link = document.createElement('a')
      link.href = URL.createObjectURL(blob)
      link.download = `${safe}_${this._usageCsvDateSuffix()}.csv`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      URL.revokeObjectURL(link.href)
    },
    shift_month(months) {
      const from = moment(this.start_date, 'MM-DD-YYYY').add(months, 'months')
      const to = moment(this.end_date, 'MM-DD-YYYY').add(months, 'months')
      this.start_date = justDate(from)
      this.end_date = justDate(to)
      this.load_data()
    },
    update_chart() {
      if (!this.usage_data) {
        this.messages_by_date_chart = null
        return
      }
      const btd = this.usage_data.by_tenant_date
      if (this.selected_chart_tenant && btd && btd[this.selected_chart_tenant]) {
        const rows = [['Date', 'Messages']]
        btd[this.selected_chart_tenant].forEach(({ date, count }) => {
          rows.push([moment(String(date)).format('MM/DD'), Number(count || 0)])
        })
        this.messages_by_date_chart = rows.length > 1 ? rows : null
        return
      }
      const by_date = this.usage_data.by_date || []
      if (!by_date.length) {
        this.messages_by_date_chart = null
        return
      }
      const rows = [['Date', 'Outbound', 'Inbound']]
      by_date.forEach(r => {
        rows.push([moment(String(r.date)).format('MM/DD'), Number(r.outbound || 0), Number(r.inbound || 0)])
      })
      this.messages_by_date_chart = rows
    },
    _normalize_message(row) {
      return {
        ...row,
        parts: Number(row.message_parts != null ? row.message_parts : (row.est_parts != null ? row.est_parts : 1)),
        price_usd: row.price_usd != null ? Number(row.price_usd) : null,
      }
    },
    async load_data() {
      this.loading = true
      this.usage_data = null
      this.messages_by_date_chart = null
      this.selected_chart_tenant = ''
      this.table_expanded = {}
      this.messages = []
      this.messages_cursor = null
      this.messages_loaded = false
      this.msg_context = ''

      const sd = moment(this.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const ed = moment(this.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const tenant = this.selected_tenant === '' ? '' : this.selected_tenant

      const [result] = await Promise.allSettled([
        get_sms_usage(tenant, sd, ed, null),
      ])

      if (result.status === 'fulfilled') {
        try {
          this.usage_data = result.value.value.data
          this.update_chart()
        } catch (e) {
          this.usage_data = null
        }
      }

      this.loading = false
      this.has_loaded = true
    },
    async load_messages(reset) {
      if (reset) {
        this.messages = []
        this.messages_cursor = null
      }
      this.messages_loading = true

      const sd = moment(this.start_date, 'MM-DD-YYYY').format('YYYY-MM-DD')
      const ed = moment(this.end_date, 'MM-DD-YYYY').format('YYYY-MM-DD')

      const [result] = await Promise.allSettled([
        get_sms_usage_messages(sd, ed, {
          tenant: this.selected_tenant || '',
          status: this.msg_status || '',
          direction: this.msg_direction || '',
          context: this.msg_context || '',
          limit: 100,
          cursor: reset ? null : this.messages_cursor,
        }),
      ])

      if (result.status === 'fulfilled') {
        try {
          const data = result.value.value.data
          this.messages = this.messages.concat((data.messages || []).map(this._normalize_message))
          this.messages_cursor = data.cursor || null
        } catch (e) {
          this.messages_cursor = null
        }
      }

      this.messages_loading = false
      this.messages_loaded = true
    },
  }
}
</script>
